import { useQueryClient } from '@tanstack/react-query';
import { Form, Formik, FormikHelpers } from 'formik';
import { motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { IoSave } from 'react-icons/io5';
import { toast } from 'react-toastify';
import { applicationAnimVariants } from '../../constants/animationVariants';
import { SELECT_FIELD_DEFAULT_VALUE } from '../../constants/settings';
import { useApplicationContext } from '../../contexts/ApplicationProvider';
import { useAuthContext } from '../../contexts/AuthProvider';
import useApplicationActions from '../../hooks/APIHooks/users/useApplicationActions';
import useCitiesActions from '../../hooks/APIHooks/users/useCitiesActions';
import usePostalCodesActions from '../../hooks/APIHooks/users/usePostalCodesActions';
import useProvincesActions from '../../hooks/APIHooks/users/useProvincesActions';
import { saveContactInfoSchema } from '../../schemas';
import { ApplicationStatus, UpdateAppReqBody } from '../../types/applications';
import { fetchErrorMessage } from '../../utils/fetchErrorMessage';
import AcademiaButton from '../CommonComps/Buttons/AcademiaButton';
import AcademiaCountrySelect from '../CommonComps/Inputs/AcademiaCountrySelect';
import AcademiaFormSelect from '../CommonComps/Inputs/AcademiaFormSelect';
import AcademiaInputField from '../CommonComps/Inputs/AcademiaInputField';
import AcademiaTextAreaField from '../CommonComps/Inputs/AcademiaTextAreaField';

interface ICorporateInfoProps {
  defaultCountryCode?: string;
  applicationDetails: ApplicationStatus | null;
}

const ApplicationContactInfo = ({
  defaultCountryCode,
  applicationDetails,
}: ICorporateInfoProps) => {
  // integration of react hooks here
  const [hasCountryReset, setHasCountryReset] = useState<boolean>(false);
  const [hasProvinceReset, setHasProvinceReset] = useState<boolean>(false);
  const [hasCityReset, setHasCityReset] = useState<boolean>(false);

  const [selectedProvinceId, setSelectedProvinceId] = useState<number>(0);
  const [selectedCityId, setSelectedCityId] = useState<number>(0);

  useEffect(() => {
    if (!applicationDetails?.user_profile) return;

    setSelectedProvinceId(applicationDetails.user_profile.province || 0);
    setSelectedCityId(applicationDetails.user_profile.city || 0);
  }, [applicationDetails]);

  // integration of context hooks here
  const { selectedCountry, setSelectedCountry, setSelectedCountryCode } =
    useApplicationContext();

  // integration custom API hooks here
  const { getCountryWiseAddedProvincesHandler } = useProvincesActions(
    '',
    undefined,
    selectedCountry.code
  );
  const { getProvinceWiseCitiesHandler } = useCitiesActions(
    '',
    selectedProvinceId ? selectedProvinceId : undefined
  );
  const { getPostalCodesWithCityIdHandler } = usePostalCodesActions(
    '',
    selectedCityId ? selectedCityId : undefined
  );

  const { updateApplicationHandler } = useApplicationActions();

  // integration of custom hooks here
  const { currentUser } = useAuthContext();

  // integration of tanstack hooks here
  const queryClient = useQueryClient();

  const provinceOptions = useMemo(() => {
    if (!getCountryWiseAddedProvincesHandler.data?.is_success) return [];

    return getCountryWiseAddedProvincesHandler.data.data.map((province) => ({
      id: province.id,
      name: province.province_name,
    }));
  }, [getCountryWiseAddedProvincesHandler.data]);

  const cityOptions = useMemo(() => {
    if (!getProvinceWiseCitiesHandler.data?.is_success) return [];

    return getProvinceWiseCitiesHandler.data.data.map((city) => ({
      id: city.id,
      name: city.city_name,
    }));
  }, [getProvinceWiseCitiesHandler.data]);

  const postalCodeOptions = useMemo(() => {
    if (!getPostalCodesWithCityIdHandler.data?.is_success) return [];

    return getPostalCodesWithCityIdHandler.data.data[0].postal_codes.map(
      (postalCode, index) => ({
        id: index,
        name: postalCode,
      })
    );
  }, [getPostalCodesWithCityIdHandler.data]);

  useEffect(() => {
    if (selectedCountry.code && selectedCountry.code !== defaultCountryCode)
      setHasCountryReset(true);
  }, [selectedCountry.code, defaultCountryCode]);

  console.log(
    'SELECT FIELD VALUES: ',
    selectedCountry.code,
    defaultCountryCode
  );

  // function to save application details declared here
  const saveApplicationHandler = async (
    values: any,
    actions: FormikHelpers<any>
  ) => {
    try {
      actions.setSubmitting(true);

      if (!applicationDetails || !currentUser) return;

      const { user, registration_request } = applicationDetails || {};

      console.log('APPLICATION FORM VALUES: ', values);

      const body: UpdateAppReqBody = {
        user: {
          id: user.id,
          country: values.country,
          updated_by: currentUser.username,
        },
        user_profile: {
          isd_code: values.isd_code,
          phone: values.phone,
          city: values.city.id,
          postal_code: values.postal_code.name,
          address: values.address,
          updated_by: currentUser.username,
        },
        registration_request: {
          id: registration_request.id,
          updated_by: user.username,
          is_resubmit:
            registration_request.status === 'resubmit' ? true : false,
          is_view: false,
          user: user.id,
        },
      };

      await updateApplicationHandler
        .mutateAsync({
          uuid: applicationDetails.user.uid,
          body,
        })
        .then((res) => {
          if (res.is_success) {
            toast.success('Application Updated!!');

            queryClient.invalidateQueries({ queryKey: ['application-status'] });
          } else {
            toast.error(res.message || 'Application Submission Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      actions.setSubmitting(false);
    }
  };

  // rendering application contact info component here
  return (
    <motion.div
      variants={applicationAnimVariants}
      initial='initial'
      animate='animate'
      className='px-5 py-4 shadow-academia-sm rounded-xl'
    >
      <Formik
        initialValues={{
          email: applicationDetails?.user.email || '',
          country: applicationDetails?.user_profile?.country || '',
          phone: applicationDetails?.user_profile?.phone || '',
          isd_code: applicationDetails?.user_profile?.isd_code || '',
          province: applicationDetails?.user_profile?.province
            ? {
                id: applicationDetails.user_profile.province,
                name: applicationDetails.user_profile.province_name,
              }
            : SELECT_FIELD_DEFAULT_VALUE,
          city: applicationDetails?.user_profile?.city
            ? {
                id: applicationDetails.user_profile.city,
                name: applicationDetails.user_profile.city_name,
              }
            : SELECT_FIELD_DEFAULT_VALUE,
          postal_code: applicationDetails?.user_profile?.postal_code
            ? {
                id: 9999,
                name: applicationDetails.user_profile.postal_code,
              }
            : SELECT_FIELD_DEFAULT_VALUE,
          address: applicationDetails?.user_profile?.address || '',
        }}
        validationSchema={saveContactInfoSchema}
        onSubmit={saveApplicationHandler}
      >
        {(props) => (
          <Form className='grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-5'>
            <AcademiaInputField
              type='email'
              name='email'
              label={`Applicant's Email`}
              placeholder='Enter your email here...'
              isRequired
              isDisabled
            />

            <AcademiaInputField
              type='number'
              name='phone'
              label={`Applicant's Phone Number`}
              placeholder='Enter your phone number here...'
              setCountryCode={setSelectedCountryCode}
              defaultSelectedCountryCode={selectedCountry.code}
              isRequired
            />

            <AcademiaCountrySelect
              name='country'
              label='Select Country'
              setCountry={setSelectedCountry}
              defaultCountryCode={selectedCountry.code}
              isRequired
            />

            <AcademiaFormSelect
              name='province'
              label='Select Province'
              options={provinceOptions}
              defaultValue={props.values.province}
              setValueOnChange={(province) => {
                props.setFieldValue('province', province);

                setSelectedProvinceId(province.id);
                setHasProvinceReset(true);
                setHasCityReset(true);
              }}
              resetSelectField={hasCountryReset}
              setResetSelectField={() => {
                props.setFieldValue('province', SELECT_FIELD_DEFAULT_VALUE);

                setHasCountryReset(false);
                setSelectedProvinceId(0);
              }}
              showSearchInput
              isDisabled={!getCountryWiseAddedProvincesHandler.data?.is_success}
              isRequired
            />

            <AcademiaFormSelect
              name='city'
              label='Select City'
              options={cityOptions}
              defaultValue={props.values.city}
              setValueOnChange={(city) => {
                props.setFieldValue('city', city);

                setSelectedCityId(city.id);
                setHasCityReset(true);
              }}
              resetSelectField={hasCountryReset || hasProvinceReset}
              setResetSelectField={() => {
                props.setFieldValue('city', SELECT_FIELD_DEFAULT_VALUE);

                setHasCountryReset(false);
                setHasProvinceReset(false);
                setSelectedCityId(0);
              }}
              showSearchInput
              isDisabled={!getProvinceWiseCitiesHandler.data?.is_success}
              isRequired
            />

            <AcademiaFormSelect
              name='postal_code'
              label='Select Postal Code'
              options={postalCodeOptions}
              defaultValue={props.values.postal_code}
              resetSelectField={
                hasCountryReset || hasProvinceReset || hasCityReset
              }
              setValueOnChange={(postalCode) =>
                props.setFieldValue('postal_code', postalCode)
              }
              setResetSelectField={() => {
                setHasCountryReset(false);
                setHasProvinceReset(false);
                setHasCityReset(false);
                props.setFieldValue('postal_code', SELECT_FIELD_DEFAULT_VALUE);
              }}
              showSearchInput
              isDisabled={!getPostalCodesWithCityIdHandler.data?.is_success}
              isRequired
            />

            <div className='md:col-span-2 space-y-6'>
              <AcademiaTextAreaField
                name='address'
                label={`Applicant's Address`}
                placeholder='Enter your address here...'
                isRequired
              />

              <div className='flex items-center justify-end'>
                <AcademiaButton
                  title={
                    props.values.province.id === -1
                      ? 'Please, select a province first!'
                      : props.values.city.id === -1
                      ? 'Please, select a city first!'
                      : props.values.postal_code.id === -1
                      ? 'Please, select a postal code first!'
                      : undefined
                  }
                  type='submit'
                  variant='premium'
                  isLoading={props.isSubmitting}
                  icon={<IoSave className='size-6' />}
                  additionalClassNames='w-full md:w-fit'
                  isDisabled={
                    props.values.province.id === -1 ||
                    props.values.city.id === -1 ||
                    props.values.postal_code.id === -1
                  }
                >
                  Save Application
                </AcademiaButton>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </motion.div>
  );
};

export default ApplicationContactInfo;
